/**
 * PricingScheduleSection — Reusable pricing regime form section
 *
 * Sprint: SPRINT-30
 * Feature: F016 — Create Facility Modal Section 3 (Pricing Schedule)
 *
 * Usage:
 *   mode='form'   — Create modal (full form inputs)
 *   mode='inline' — Financial tab (inline-edit, used by F018)
 *
 * Each regime row supports:
 *   - Payment type: IO / P&I (radio)
 *   - Rate type: Fixed / Variable (radio)
 *   - Entry mode: Composed (base + margin → total) or Flat (total only)
 *   - Effective from / to (date inputs)
 *   - Regime months (number input)
 *   - Repayment frequency (select)
 *   - Repayment override (optional number)
 *   - Amortisation period months (optional number)
 *   - Provenance chip: "from product" or "manual" (flips on edit)
 */

import { useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Plus, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PricingScheduleFormData {
  id?: string;
  sequence: number;
  paymentType: "interest_only" | "principal_and_interest";
  rateType: "fixed" | "variable";
  entryMode: "composed" | "flat";
  baseRate: string | null;
  margin: string | null;
  totalRate: string | null;
  effectiveFrom: string;
  effectiveTo: string | null;
  regimeMonths: number | null;
  repaymentFrequency: string;
  repaymentOverride: string | null;
  amortisationPeriodMonths: number | null;
  source: "manual" | "product_snapshot" | "repricing_event";
  notes: string | null;
}

export interface PricingScheduleSectionProps {
  schedules: PricingScheduleFormData[];
  onChange: (schedules: PricingScheduleFormData[]) => void;
  mode?: "form" | "inline";
  onSnapshotApplied?: () => void;
}

// ---------------------------------------------------------------------------
// Default factory
// ---------------------------------------------------------------------------

function createDefaultSchedule(sequence: number, effectiveFrom = ""): PricingScheduleFormData {
  return {
    sequence,
    paymentType: "interest_only",
    rateType: "fixed",
    entryMode: "composed",
    baseRate: null,
    margin: null,
    totalRate: null,
    effectiveFrom,
    effectiveTo: null,
    regimeMonths: null,
    repaymentFrequency: "monthly",
    repaymentOverride: null,
    amortisationPeriodMonths: null,
    source: "manual",
    notes: null,
  };
}

// ---------------------------------------------------------------------------
// Auto-compute total rate from base + margin
// ---------------------------------------------------------------------------

function computeTotalRate(baseRate: string | null, margin: string | null): string | null {
  const base = parseFloat(baseRate ?? "");
  const marg = parseFloat(margin ?? "");
  if (isNaN(base) || isNaN(marg)) return null;
  // Avoid floating-point noise: round to 4 decimal places
  const total = Math.round((base + marg) * 10000) / 10000;
  return String(total);
}

// ---------------------------------------------------------------------------
// Provenance chip label
// ---------------------------------------------------------------------------

function provenanceLabel(source: PricingScheduleFormData["source"]): string {
  if (source === "product_snapshot") return "from product";
  if (source === "repricing_event") return "repricing";
  return "manual";
}

// ---------------------------------------------------------------------------
// Individual regime row
// ---------------------------------------------------------------------------

interface RegimeRowProps {
  schedule: PricingScheduleFormData;
  isOnly: boolean;
  onUpdate: (updated: PricingScheduleFormData) => void;
  onRemove: () => void;
}

function RegimeRow({ schedule, isOnly, onUpdate, onRemove }: RegimeRowProps) {
  const seq = schedule.sequence;

  // Atomic field updater — marks as manual when source was product_snapshot
  const updateField = useCallback(
    <K extends keyof PricingScheduleFormData>(
      field: K,
      value: PricingScheduleFormData[K],
    ) => {
      const updated: PricingScheduleFormData = {
        ...schedule,
        [field]: value,
        // Flip source to manual if user edits a product-snapshot regime
        source:
          schedule.source === "product_snapshot" ? "manual" : schedule.source,
      };
      onUpdate(updated);
    },
    [schedule, onUpdate],
  );

  // Entry mode toggle — atomic update that also clears irrelevant fields
  const handleEntryModeChange = useCallback(
    (mode: "composed" | "flat") => {
      const updated: PricingScheduleFormData = {
        ...schedule,
        entryMode: mode,
        // Clear fields that are irrelevant to the new mode
        baseRate: mode === "flat" ? null : schedule.baseRate,
        margin: mode === "flat" ? null : schedule.margin,
        totalRate: mode === "flat" ? schedule.totalRate : schedule.totalRate,
        source:
          schedule.source === "product_snapshot" ? "manual" : schedule.source,
      };
      onUpdate(updated);
    },
    [schedule, onUpdate],
  );

  // Composed rate: auto-compute total when base or margin changes
  const handleBaseRateChange = useCallback(
    (value: string) => {
      const newBase = value === "" ? null : value;
      const newTotal = computeTotalRate(newBase, schedule.margin);
      onUpdate({
        ...schedule,
        baseRate: newBase,
        totalRate: newTotal,
        source:
          schedule.source === "product_snapshot" ? "manual" : schedule.source,
      });
    },
    [schedule, onUpdate],
  );

  const handleMarginChange = useCallback(
    (value: string) => {
      const newMargin = value === "" ? null : value;
      const newTotal = computeTotalRate(schedule.baseRate, newMargin);
      onUpdate({
        ...schedule,
        margin: newMargin,
        totalRate: newTotal,
        source:
          schedule.source === "product_snapshot" ? "manual" : schedule.source,
      });
    },
    [schedule, onUpdate],
  );

  const isComposed = schedule.entryMode === "composed";

  return (
    <div
      data-testid={`pricing-schedule-row-${seq}`}
      className="border rounded-lg p-4 space-y-4 relative"
    >
      {/* Header row: sequence number + provenance chip + remove button */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-muted-foreground">
          Regime {seq}
        </span>
        <div className="flex items-center gap-2">
          <Badge
            variant={schedule.source === "product_snapshot" ? "secondary" : "outline"}
            data-testid={`pricing-schedule-${seq}-provenance-chip`}
            className="text-xs"
          >
            {provenanceLabel(schedule.source)}
          </Badge>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid={`pricing-schedule-${seq}-remove`}
            disabled={isOnly}
            onClick={onRemove}
            aria-label={`Remove regime ${seq}`}
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </div>

      {/* Row 1: Payment type + Rate type */}
      <div className="grid grid-cols-2 gap-4">
        {/* Payment type */}
        <div className="space-y-2">
          <Label className="text-xs font-medium">Payment Type</Label>
          <RadioGroup
            data-testid={`pricing-schedule-${seq}-payment-type`}
            value={schedule.paymentType}
            onValueChange={(value) =>
              updateField(
                "paymentType",
                value as "interest_only" | "principal_and_interest",
              )
            }
            className="flex gap-4"
          >
            <div className="flex items-center gap-1.5">
              <RadioGroupItem
                value="interest_only"
                id={`ps-${seq}-io`}
                data-testid={`pricing-schedule-${seq}-payment-type-io`}
              />
              <Label htmlFor={`ps-${seq}-io`} className="text-sm cursor-pointer">
                IO
              </Label>
            </div>
            <div className="flex items-center gap-1.5">
              <RadioGroupItem
                value="principal_and_interest"
                id={`ps-${seq}-pi`}
                data-testid={`pricing-schedule-${seq}-payment-type-pi`}
              />
              <Label htmlFor={`ps-${seq}-pi`} className="text-sm cursor-pointer">
                P&amp;I
              </Label>
            </div>
          </RadioGroup>
        </div>

        {/* Rate type */}
        <div className="space-y-2">
          <Label className="text-xs font-medium">Rate Type</Label>
          <RadioGroup
            data-testid={`pricing-schedule-${seq}-rate-type`}
            value={schedule.rateType}
            onValueChange={(value) =>
              updateField("rateType", value as "fixed" | "variable")
            }
            className="flex gap-4"
          >
            <div className="flex items-center gap-1.5">
              <RadioGroupItem
                value="fixed"
                id={`ps-${seq}-fixed`}
                data-testid={`pricing-schedule-${seq}-rate-type-fixed`}
              />
              <Label htmlFor={`ps-${seq}-fixed`} className="text-sm cursor-pointer">
                Fixed
              </Label>
            </div>
            <div className="flex items-center gap-1.5">
              <RadioGroupItem
                value="variable"
                id={`ps-${seq}-variable`}
                data-testid={`pricing-schedule-${seq}-rate-type-variable`}
              />
              <Label htmlFor={`ps-${seq}-variable`} className="text-sm cursor-pointer">
                Variable
              </Label>
            </div>
          </RadioGroup>
        </div>
      </div>

      {/* Row 2: Entry mode toggle */}
      <div className="space-y-2">
        <Label className="text-xs font-medium">Rate Entry Mode</Label>
        <div
          data-testid={`pricing-schedule-${seq}-entry-mode`}
          className="flex gap-1 border rounded-md p-0.5 w-fit bg-muted"
        >
          <button
            type="button"
            data-testid={`pricing-schedule-${seq}-entry-mode-composed`}
            onClick={() => handleEntryModeChange("composed")}
            className={cn(
              "px-3 py-1 text-sm rounded transition-colors",
              isComposed
                ? "bg-background shadow-sm text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            Composed
          </button>
          <button
            type="button"
            data-testid={`pricing-schedule-${seq}-entry-mode-flat`}
            onClick={() => handleEntryModeChange("flat")}
            className={cn(
              "px-3 py-1 text-sm rounded transition-colors",
              !isComposed
                ? "bg-background shadow-sm text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            Flat
          </button>
        </div>
      </div>

      {/* Row 3: Rate fields */}
      <div className="grid grid-cols-3 gap-3">
        {/* Base rate — composed mode only */}
        {isComposed && (
          <div className="space-y-1">
            <Label htmlFor={`ps-${seq}-base-rate`} className="text-xs">
              Base Rate %
            </Label>
            <Input
              id={`ps-${seq}-base-rate`}
              data-testid={`pricing-schedule-${seq}-base-rate`}
              type="number"
              step="0.01"
              placeholder="4.50"
              value={schedule.baseRate ?? ""}
              onChange={(e) => handleBaseRateChange(e.target.value)}
            />
          </div>
        )}

        {/* Margin — composed mode only */}
        {isComposed && (
          <div className="space-y-1">
            <Label htmlFor={`ps-${seq}-margin`} className="text-xs">
              Margin %
            </Label>
            <Input
              id={`ps-${seq}-margin`}
              data-testid={`pricing-schedule-${seq}-margin`}
              type="number"
              step="0.01"
              placeholder="1.50"
              value={schedule.margin ?? ""}
              onChange={(e) => handleMarginChange(e.target.value)}
            />
          </div>
        )}

        {/* Total rate — always visible; read-only in composed mode */}
        <div className="space-y-1">
          <Label htmlFor={`ps-${seq}-total-rate`} className="text-xs">
            {isComposed ? "Total Rate % (auto)" : "Total Rate %"}
          </Label>
          <Input
            id={`ps-${seq}-total-rate`}
            data-testid={`pricing-schedule-${seq}-total-rate`}
            type="number"
            step="0.01"
            placeholder={isComposed ? "auto" : "6.00"}
            value={schedule.totalRate ?? ""}
            readOnly={isComposed}
            className={isComposed ? "bg-muted cursor-default" : ""}
            onChange={
              isComposed
                ? undefined
                : (e) =>
                    updateField("totalRate", e.target.value === "" ? null : e.target.value)
            }
          />
        </div>
      </div>

      {/* Row 4: Dates + regime months */}
      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-1">
          <Label htmlFor={`ps-${seq}-effective-from`} className="text-xs">
            Effective From
          </Label>
          <Input
            id={`ps-${seq}-effective-from`}
            data-testid={`pricing-schedule-${seq}-effective-from`}
            type="date"
            value={schedule.effectiveFrom}
            onChange={(e) => updateField("effectiveFrom", e.target.value)}
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor={`ps-${seq}-effective-to`} className="text-xs">
            Effective To
          </Label>
          <Input
            id={`ps-${seq}-effective-to`}
            data-testid={`pricing-schedule-${seq}-effective-to`}
            type="date"
            value={schedule.effectiveTo ?? ""}
            onChange={(e) =>
              updateField("effectiveTo", e.target.value === "" ? null : e.target.value)
            }
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor={`ps-${seq}-regime-months`} className="text-xs">
            Regime Months
          </Label>
          <Input
            id={`ps-${seq}-regime-months`}
            data-testid={`pricing-schedule-${seq}-regime-months`}
            type="number"
            min={1}
            placeholder="12"
            value={schedule.regimeMonths ?? ""}
            onChange={(e) =>
              updateField(
                "regimeMonths",
                e.target.value === "" ? null : parseInt(e.target.value, 10),
              )
            }
          />
        </div>
      </div>

      {/* Row 5: Repayment frequency + override + amortisation */}
      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Repayment Frequency</Label>
          <Select
            value={schedule.repaymentFrequency}
            onValueChange={(value) => updateField("repaymentFrequency", value)}
          >
            <SelectTrigger
              data-testid={`pricing-schedule-${seq}-repayment-frequency`}
              className="text-sm"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="fortnightly">Fortnightly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="quarterly">Quarterly</SelectItem>
              <SelectItem value="annually">Annually</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label htmlFor={`ps-${seq}-repayment-override`} className="text-xs">
            Repayment Override
          </Label>
          <Input
            id={`ps-${seq}-repayment-override`}
            data-testid={`pricing-schedule-${seq}-repayment-override`}
            type="number"
            step="0.01"
            placeholder="Optional"
            value={schedule.repaymentOverride ?? ""}
            onChange={(e) =>
              updateField(
                "repaymentOverride",
                e.target.value === "" ? null : e.target.value,
              )
            }
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor={`ps-${seq}-amortisation-period`} className="text-xs">
            Amortisation (months)
          </Label>
          <Input
            id={`ps-${seq}-amortisation-period`}
            data-testid={`pricing-schedule-${seq}-amortisation-period`}
            type="number"
            min={1}
            placeholder="Optional"
            value={schedule.amortisationPeriodMonths ?? ""}
            onChange={(e) =>
              updateField(
                "amortisationPeriodMonths",
                e.target.value === "" ? null : parseInt(e.target.value, 10),
              )
            }
          />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main section component
// ---------------------------------------------------------------------------

export function PricingScheduleSection({
  schedules,
  onChange,
  mode = "form",
  onSnapshotApplied: _onSnapshotApplied,
}: PricingScheduleSectionProps) {
  const handleAddRegime = useCallback(() => {
    const last = schedules[schedules.length - 1];
    // Contiguity: auto-set new regime's effectiveFrom = prev regime's effectiveTo
    const newEffectiveFrom = last?.effectiveTo ?? "";
    const newSeq = (last?.sequence ?? 0) + 1;
    onChange([...schedules, createDefaultSchedule(newSeq, newEffectiveFrom)]);
  }, [schedules, onChange]);

  const handleUpdateRegime = useCallback(
    (index: number, updated: PricingScheduleFormData) => {
      const next = [...schedules];
      next[index] = updated;
      onChange(next);
    },
    [schedules, onChange],
  );

  const handleRemoveRegime = useCallback(
    (index: number) => {
      if (schedules.length <= 1) return;
      const next = schedules.filter((_, i) => i !== index);
      // Re-sequence after removal
      const resequenced = next.map((s, i) => ({ ...s, sequence: i + 1 }));
      onChange(resequenced);
    },
    [schedules, onChange],
  );

  return (
    <div
      data-testid="pricing-schedule-section"
      className={cn("space-y-3", mode === "inline" && "border-t pt-4")}
    >
      {schedules.map((schedule, index) => (
        <RegimeRow
          key={schedule.sequence}
          schedule={schedule}
          isOnly={schedules.length === 1}
          onUpdate={(updated) => handleUpdateRegime(index, updated)}
          onRemove={() => handleRemoveRegime(index)}
        />
      ))}

      <Button
        type="button"
        variant="outline"
        size="sm"
        data-testid="pricing-schedule-add-regime"
        onClick={handleAddRegime}
        className="w-full"
      >
        <Plus className="h-4 w-4 mr-1" />
        Add Regime
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Default export + re-export types
// ---------------------------------------------------------------------------

export default PricingScheduleSection;

/**
 * Create a default pricing schedule array with one initial regime.
 * Convenience factory for form initialisation.
 */
export function createDefaultSchedules(
  effectiveFrom = "",
  source: PricingScheduleFormData["source"] = "manual",
): PricingScheduleFormData[] {
  const s = createDefaultSchedule(1, effectiveFrom);
  return [{ ...s, source }];
}
